import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { FilterContext, Project, Task } from "@todoer/shared";
import { db, getMeta, useCollaborators, useMemberships, useUser } from "./db.js";

/** Everything the filter language needs, built from the local store. */
export function useFilterContext(): FilterContext | null {
  const user = useUser();
  const collaborators = useCollaborators();
  const memberships = useMemberships();
  const data = useLiveQuery(async () => {
    const [projects, sections, labels] = await Promise.all([db.projects.toArray(), db.sections.toArray(), db.labels.toArray()]);
    return { projects, sections, labels };
  }, []);
  return useMemo(() => {
    if (!user || !data) return null;
    const memberCounts: Record<string, number> = {};
    for (const m of memberships) memberCounts[m.projectId] = (memberCounts[m.projectId] ?? 0) + 1;
    return { userId: user.id, ...data, collaborators, memberCounts, startOfWeek: user.startOfWeek };
  }, [user, data, collaborators, memberships]);
}

/** Active (not completed) tasks. */
export function useActiveTasks(): Task[] | undefined {
  return useLiveQuery(() => db.tasks.filter((t) => !t.completedAt).toArray(), []);
}

/** Projects sorted Inbox first, then by tree order, with depth for indentation. */
export function sortProjectTree(projects: Project[]): (Project & { depth: number })[] {
  const byParent = new Map<string | null, Project[]>();
  for (const p of projects) {
    const key = p.parentId && projects.some((x) => x.id === p.parentId) ? p.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), p]);
  }
  const out: (Project & { depth: number })[] = [];
  const walk = (parent: string | null, depth: number) => {
    const kids = (byParent.get(parent) ?? []).sort((a, b) => Number(b.isInbox) - Number(a.isInbox) || a.childOrder - b.childOrder || a.name.localeCompare(b.name));
    for (const k of kids) { out.push({ ...k, depth }); walk(k.id, depth + 1); }
  };
  walk(null, 0);
  return out;
}

export function useProjects(includeArchived = false) {
  return useLiveQuery(() => db.projects.filter((p) => includeArchived || !p.isArchived).toArray(), [includeArchived]) ?? [];
}

/** Re-renders at the start of each minute (for "today" boundaries and timers). */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Simple external store helper. */
export function createStore<T>(initial: T) {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next: T | ((prev: T) => T)) => {
      value = typeof next === "function" ? (next as (p: T) => T)(value) : next;
      listeners.forEach((l) => l());
    },
    use: () => useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, () => value),
  };
}

export { getMeta };
